import { useLanguage } from "@/hooks/use-language.ts";
import { shopQuery } from "@/hooks/use-shop.ts";
import { useUser } from "@/hooks/use-user.ts";
import { env } from "@/lib/env";
import { cn, formatCurrency, resetKiosk } from "@/lib/utils";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { Pyramid } from "lucide-react";
import { ComponentProps, ReactNode, useEffect, useRef } from "react";
import { Trans, useTranslation } from "react-i18next";
import { InvactivityModal } from "./invactivity-modal";
import { LanguagePicker } from "./language-picker";
import { AudioMuteButton } from "./audio-mute-button";
import { AudioSlot } from "@/features/audio/kiosk-audio";
import { MaintenanceToolsModal } from "./maintenance-tools";
import { Modal, ModalContent, ModalTitle, useMuiModal } from "./modal";
import { Button } from "./ui/button";
import { getFeatureFlags } from "@/lib/runtime-config";

export function Page({
  children,
  className,
  skipInactvityModal = false,
}: {
  children: ReactNode;
  className?: string;
  skipInactvityModal?: boolean;
}) {
  useTranslation();
  useInactivityModal({
    disabled:
      skipInactvityModal || !!localStorage.getItem("alwaysSkipInactivityModal"),
  });

  return (
    <div>
      <div className={cn(`min-h-screen bg-gray-50`, className)}>{children}</div>
    </div>
  );
}

export function PageContent({
  children,
  className,
  ...props
}: {
  children: ReactNode;
} & ComponentProps<"div">) {
  return (
    <div>
      <div className={cn(`max-w-4xl mx-auto`, className)} {...props}>
        {children}
      </div>
    </div>
  );
}

export function PageTitle({ children }: { children: ReactNode }) {
  return (
    <div className="text-center mb-8">
      <h1 className="text-5xl text-bub-dark-blue font-bold mb-4">{children}</h1>
    </div>
  );
}

export function Footer({
  helpText,
  attribution,
}: {
  helpText?: ReactNode;
  attribution?: ReactNode;
}) {
  const { showModal, closeModal } = useMuiModal();
  const supportPhone = getFeatureFlags().supportPhone;
  return (
    <div className="fixed bottom-8 left-8 right-8 flex flex-col items-center gap-y-3">
      <div className="flex w-full justify-between">
        <div>
          {!!helpText && (
            <div
              className="flex items-center font-fancy text-[26px] gap-2"
              onClick={() =>
                showModal(
                  <Modal>
                    <AudioSlot slot="modal.help" />
                    <ModalTitle>
                      <Trans>Segítség</Trans>
                    </ModalTitle>
                    <ModalContent>
                      <p className="text-center tracking-tight">{helpText}</p>
                      <Button
                        className={`font-helvetica text-[40px]`}
                        onClick={closeModal}
                      >
                        <Trans>Ok</Trans>
                      </Button>
                    </ModalContent>
                  </Modal>,
                )
              }
            >
              <img src="/question-mark.svg" />
              <Trans>Segítség</Trans>
            </div>
          )}
        </div>
        <div
          className="flex flex-col"
          onClick={() =>
            showModal(
              <Modal>
                <AudioSlot slot="modal.tech-support" />
                <ModalTitle>
                  <Trans>Technikai támogatás</Trans>
                </ModalTitle>
                <ModalContent className="relative">
                  <p>
                    <Trans>Technikai segítség nyújtásra van szüksége?</Trans>
                  </p>
                  <p>
                    <Trans values={{ supportPhone }}>
                      Hívja 0-24-ben elérhető ügyfélszolgálatunkat a{" "}
                      {{ supportPhone }} telefonszámon!
                    </Trans>
                  </p>
                  <Button
                    onClick={closeModal}
                    className={`font-helvetica text-[40px] mt-4`}
                  >
                    <Trans>Ok</Trans>
                  </Button>
                  <OpenMaintenanceToolsButton />
                </ModalContent>
              </Modal>,
            )
          }
        >
          <img src="/bubbles-logo-on-white.svg" className="w-[180px]" />
          <span className="ml-auto text-sm mr-[12px]">
            v3.0.2{env.VITE_MODE === "development" && "-dev"}
          </span>
        </div>
      </div>
      {attribution}
    </div>
  );
}

function OpenMaintenanceToolsButton() {
  const { showModal } = useMuiModal();
  const handler = useOnQuintupleClick(() => {
    showModal(<MaintenanceToolsModal />);
  });

  return (
    <div
      className="h-16 w-16 absolute right-0 bottom-0"
      onClick={() => {
        handler.recordClick();
      }}
    />
  );
}

function useOnQuintupleClick(action: () => void) {
  const timerRef = useRef<{ clicks: number }>(null);
  const timeoutRef = useRef<any>(null);
  const debounceMs = 2000;

  return {
    recordClick: () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timerRef.current = { clicks: (timerRef.current?.clicks ?? 0) + 1 };
      if (timerRef.current.clicks >= 5) {
        timerRef.current = { clicks: 0 };
        action();
      }
      timeoutRef.current = setTimeout(() => {
        timerRef.current = { clicks: 0 };
      }, debounceMs);
    },
  };
}

export function Header({
  backArrowHref,
  backArrowTitle = "Vissza",
  variant,
  showUserDetails = true,
  children,
  params,
  hideLanguagePicker,
}: {
  backArrowHref?: string | ReactNode;
  backArrowTitle?: string;
  // defaults to "small" when there's nothing in the header, "large" otherwise
  variant?: "small" | "medium" | "large";
  showUserDetails?: boolean;
  children?: ReactNode;
  params?: Record<string, string>;
  hideLanguagePicker?: boolean;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const size = variant ?? (children ? "large" : "small");

  return (
    <>
      <header
        className={cn(
          `relative bg-[url('/background-wave-top.svg')] bg-no-repeat bg-cover bg-bottom pt-8 w-full h-[33vh]`,
          { "h-[20vh]": size === "small", "h-[25vh]": size === "medium" },
        )}
      >
        <div className="w-full flex items-start justify-between px-8 absolute">
          <div>
            {typeof backArrowHref === "string" ? (
              <BackButton
                label={t(backArrowTitle)}
                onClick={() => {
                  navigate({
                    href: backArrowHref,
                    params: params as any,
                    reloadDocument: true,
                  });
                }}
              />
            ) : (
              backArrowHref
            )}
          </div>
          <div className="flex items-center gap-8">
            {showUserDetails && <HeaderUserDetails />}
            {!hideLanguagePicker && (
              <>
                <AudioMuteButton />
                <LanguagePicker />
              </>
            )}
          </div>
        </div>
        {children}
      </header>
    </>
  );
}

/** The logged in user's name and balance, top right in the headers. */
export function HeaderUserDetails() {
  const search = useSearch({ from: "__root__" });
  const { data: user } = useUser();
  const { data: shop } = useQuery(shopQuery(search.kioskKey));
  const lang = useLanguage();

  if (!user) {
    return null;
  }

  return (
    <div className="flex items-center gap-6 text-[30px] font-helvetica">
      <span className="flex items-center gap-3 !text-white">
        <img src="/user-avatar.svg" className="size-12 mb-1" />
        {lang === "hu"
          ? `${user.lastName} ${user.firstName}`
          : `${user.firstName} ${user.lastName}`}
      </span>
      <span className="flex items-center gap-3 !text-white">
        <img src="/wallet-white.svg" className="size-12 mb-1" />
        {formatCurrency(user.balance, shop?.currencyCode ?? "HUF")}
      </span>
    </div>
  );
}

type BackButtonProps = {
  label: string;
  onClick: () => void;
};

export function BackButton({ label, onClick }: BackButtonProps) {
  return (
    <div
      onClick={onClick}
      className="flex gap-3 items-center text-5xl font-helvetica text-white cursor-pointer mb-8"
    >
      <img src="/back-arrow-white.svg" className="size-16 mb-2" />
      {label}
    </div>
  );
}

function useAngularNavSync() {
  const iframeRef = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    const interval = setInterval(() => {
      if (iframeRef.current) {
        const iframePathname =
          iframeRef.current.contentWindow?.location.pathname.replace(
            "/angular-iframe",
            "",
          );
        if (
          iframePathname &&
          iframePathname !== location.pathname &&
          iframePathname !== "/blank"
        ) {
          document.location.href = iframePathname;
        }
      }
    }, 250);
    return () => clearInterval(interval);
  }, []);

  return iframeRef;
}

function useInactivityModal(opts?: { disabled?: boolean }) {
  const inactivityTimeout = 45000;
  const { showModal } = useMuiModal();

  useEffect(() => {
    if (opts?.disabled) {
      return;
    }
    const action = () => {
      showModal(<InvactivityModal />);
    };
    let timeout = setTimeout(action, inactivityTimeout);

    const handler = () => {
      clearTimeout(timeout);
      timeout = setTimeout(action, inactivityTimeout);
    };
    document.addEventListener("click", handler);
    return () => {
      document.removeEventListener("click", handler);
      clearTimeout(timeout);
    };
  }, [opts?.disabled]);
}

export function useLogout() {
  const { showModal, closeModal } = useMuiModal();
  const navigate = useNavigate();

  const handleLogout = async () => {
    const coupon = sessionStorage.getItem("coupon");
    let isLoggingOut: boolean | undefined = true;

    if (coupon) {
      isLoggingOut = await showModal<boolean>(
        <Modal>
          <AudioSlot slot="modal.logout-coupon-warning" />
          <ModalTitle>
            <Trans>Figyelmeztetés!</Trans>
          </ModalTitle>
          <ModalContent className="px-14">
            <p className="mb-6">
              <Trans>
                Amennyiben kijelentkezel, elveszíted érvényesített kuponodat!
              </Trans>
            </p>
            <div className="flex gap-6">
              <Button
                onClick={() => closeModal(false)}
                variant="outline"
                className="px-6"
              >
                <Trans>Vissza</Trans>
              </Button>
              <Button onClick={() => closeModal(true)}>
                <Trans>Értettem, tovább</Trans>
              </Button>
            </div>
          </ModalContent>
        </Modal>,
      );
    }

    if (isLoggingOut) {
      await resetKiosk({ endReason: "logout" });
      sessionStorage.setItem("can_deactivate", "true");
      navigate({ to: "/start-screen", reloadDocument: true });
    }
  };

  return { handleLogout };
}
